(function initSuoDiagnosisApp() {
  "use strict";

  const data = window.SUO_DIAG_DATA;
  const app = document.getElementById("suo-diag-app");
  const logger = window.SUO_DIAG_LOGGER;

  // Resolve a bundled illustration. Standalone (index.html) uses the relative
  // assets/images path; when embedded in a Shopify theme, the section injects
  // window.SUO_DIAG_ASSETS = { "q1.png": "<asset_url>", ... } so the same names
  // resolve to the theme's CDN assets instead of a broken relative path.
  function assetUrl(name) {
    const assets = window.SUO_DIAG_ASSETS || {};
    return assets[name] || ("assets/images/" + name);
  }

  const state = {
    step: 0,
    userType: null,
    answers: {},
    picks: {},
    history: [],
    recommendations: []
  };

  function trackEvent(eventName, params) {
    if (window.gtag) {
      window.gtag("event", eventName, params || {});
    }
  }

  function logEvent(eventName, params) {
    if (logger) logger.send(eventName, params || {});
  }

  function getSelectedTags() {
    return [state.userType].concat(Object.values(state.answers)).filter(Boolean);
  }

  function getFlow() {
    return data.getQuestionFlow(state.userType, state.answers);
  }

  // Label of the picked gender option ("女性"/"男性"/"回答しない"), or "" when the
  // question was not asked or not answered. Feeds the 性別 column in the log sheet.
  function genderLabel() {
    const pickedId = state.picks.gender;
    if (!pickedId || !data.GENDER_QUESTION) return "";
    const option = data.GENDER_QUESTION.options.find((candidate) => candidate.id === pickedId);
    return option ? option.label : "";
  }

  // Human-readable summary of the user's choices, e.g. "大人 / 通勤・通学 / 首元 / 価格".
  function readableAnswers() {
    const userOption = data.USER_OPTIONS.find((option) => option.tag === state.userType);
    const labels = userOption ? [userOption.label] : [];
    getFlow().forEach((question) => {
      const pickedId = state.picks[question.key];
      if (!pickedId) return;
      const option = question.options.find((candidate) => candidate.id === pickedId);
      if (option) labels.push(option.label);
    });
    return labels.join(" / ");
  }

  function yen(value) {
    return new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY", maximumFractionDigits: 0 }).format(value);
  }

  function getProductUrl(product) {
    return product.url;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  const USER_SUB_LABELS = {
    user_adult: "ご本人 / ご家族",
    user_child: "お子さま",
    user_pet: "犬・猫",
    user_baby: "乳幼児まわり"
  };

  const SIZE_BIG_LABELS = {
    size_s: "S",
    size_m: "M",
    size_l: "L",
    size_ll: "LL",
    size_unknown: "?"
  };

  const SIZE_CAP_LABELS = {
    size_s: "小さめ",
    size_m: "標準",
    size_l: "やや大きめ",
    size_ll: "大きめ",
    size_unknown: "わからない"
  };

  const QUESTION_SUB_LABEL = {
    usage: "シーンを選択してください",
    area: "場所を選択してください",
    sports: "動きの量を選択してください",
    cool: "好みを選択してください",
    priority: "重視したいことを選択してください",
    ownership: "あてはまる方を選択してください",
    replacement_need: "変えたいポイントを選択してください",
    extra: "あてはまる方を選択してください",
    size: "近いサイズを選択してください",
    gender: "サイズ提案の参考にします（任意）",
    pet_size: "近い大きさを選択してください"
  };

  function renderHero(compact) {
    const cls = compact ? "suo-diag-hero is-compact" : "suo-diag-hero";
    return `
      <div class="${cls}" role="img" aria-label="SUO クール診断">
        <picture>
          <source media="(max-width: 540px)" srcset="${assetUrl('diagnosis-illustrations_sp.webp')}">
          <img src="${assetUrl('diagnosis-illustrations.webp')}" alt="SUO クール診断" onerror="this.style.display='none'">
        </picture>
      </div>
    `;
  }

  function renderShell(content, modifier) {
    app.className = `suo-diag-app ${modifier || ""}`;
    app.innerHTML = `<section class="suo-diag-shell">${content}</section>`;
  }

  function renderIntro() {
    renderShell(`
      ${renderHero(false)}
      <div class="suo-diag-card">
        <div class="suo-diag-start-intro">
          <p>サイズ、用途、買い替えの迷いまで。<br>いくつかの質問にこたえるだけで、SUO の中からあなたにぴったりの一本をご提案します。</p>
          <div class="suo-diag-start-meta">
            <span>所要 約3分</span>
            <span>登録不要</span>
          </div>
        </div>
        <button class="suo-diag-primary" type="button" data-action="begin">診断を始める</button>
      </div>
    `, "suo-diag-intro");
  }

  function renderStart() {
    const options = data.USER_OPTIONS.map((option) => {
      const isSelected = state.userType === option.tag;
      const sub = USER_SUB_LABELS[option.tag] || "";
      const label = sub ? `${escapeHtml(option.label)}<small>${escapeHtml(sub)}</small>` : escapeHtml(option.label);
      return `
        <button class="suo-diag-choice${isSelected ? " is-selected" : ""}" type="button" data-user="${option.tag}" aria-pressed="${isSelected}">
          <span class="suo-diag-choice-label">${label}</span>
          <span class="suo-diag-choice-arrow" aria-hidden="true">›</span>
        </button>
      `;
    }).join("");

    const total = data.getMaxFlowLength(state.userType || "user_adult") + 1;
    renderShell(`
      ${renderProgress(0, total)}
      <div class="suo-diag-card" data-q-key="user">
        <div class="suo-diag-question-body">
          <div class="suo-diag-question-head-row">
            <span class="suo-diag-q-mark">Q.</span>
            <h2><span class="suo-diag-q-highlight">どなたが使いますか?</span></h2>
          </div>
          <div class="suo-diag-q-illust">
            <img src="${assetUrl('q1.png')}" alt="" onerror="this.closest('.suo-diag-q-illust').remove()">
          </div>
          <p class="suo-diag-question-sub-label">使う方を選択してください</p>
          <div class="suo-diag-choice-grid" role="radiogroup" aria-label="使う方">
            ${options}
          </div>
          <div class="suo-diag-nav-row">
            <button class="suo-diag-secondary suo-diag-back-btn" type="button" data-action="back">前に戻る</button>
            <button class="suo-diag-primary" type="button" data-action="start" ${state.userType ? "" : "disabled"}>次へ</button>
          </div>
        </div>
      </div>
    `, "suo-diag-question");
  }

  function renderProgress(currentIndex, total) {
    const safeTotal = Math.max(1, total);
    const pct = Math.min(100, Math.round(((currentIndex + 1) / safeTotal) * 100));
    return `<div class="suo-diag-progress" role="progressbar" aria-valuenow="${currentIndex + 1}" aria-valuemin="1" aria-valuemax="${safeTotal}" aria-label="進行状況">
        <span class="suo-diag-progress-fill" style="width: ${pct}%"></span>
      </div>`;
  }

  function renderQuestion() {
    const flow = getFlow();
    const index = Math.max(0, state.step - 1);
    const question = flow[index];
    const pickedId = state.picks[question.key];
    const isSize = question.key === "size";

    const options = question.options.map((option) => {
      // Highlight by the unique option id, not the tag: several questions reuse the
      // same tag across options (e.g. baby ベビーカー/チャイルドシート both = baby_car),
      // and a tag-based check would light up every option that shares the tag.
      const isSelected = pickedId === option.id;
      let inner;
      if (isSize) {
        const big = SIZE_BIG_LABELS[option.tag] || option.label;
        const cap = SIZE_CAP_LABELS[option.tag] || option.label;
        inner = `<span><span class="suo-diag-size-big">${escapeHtml(big)}</span><span class="suo-diag-size-cap">${escapeHtml(cap)}</span></span>`;
      } else {
        inner = `<span class="suo-diag-choice-label">${escapeHtml(option.label)}</span><span class="suo-diag-choice-arrow" aria-hidden="true">›</span>`;
      }
      return `
        <button class="suo-diag-choice${isSelected ? " is-selected" : ""}" type="button" data-option="${escapeHtml(option.tag)}" data-option-id="${escapeHtml(option.id)}" aria-pressed="${isSelected}">
          ${inner}
        </button>
      `;
    }).join("");

    const subLabel = QUESTION_SUB_LABEL[question.key] || "あてはまるものを選択してください";
    const isLast = state.step >= flow.length;
    const remaining = flow.length - state.step;
    const remainText = remaining > 0 ? `あと${remaining}問あります` : "これが最後の質問です";

    renderShell(`
      ${renderProgress(state.step, data.getMaxFlowLength(state.userType) + 1)}
      <div class="suo-diag-card" data-q-key="${escapeHtml(question.key)}">
        <div class="suo-diag-question-body">
          <div class="suo-diag-question-head-row">
            <span class="suo-diag-q-mark">Q.</span>
            <h2><span class="suo-diag-q-highlight">${escapeHtml(question.title)}</span></h2>
          </div>
          <p class="suo-diag-question-sub-label">${escapeHtml(subLabel)}</p>
          <div class="suo-diag-choice-grid">
            ${options}
          </div>
          <div class="suo-diag-nav-row">
            <button class="suo-diag-secondary suo-diag-back-btn" type="button" data-action="back">前に戻る</button>
            <button class="suo-diag-primary" type="button" data-action="next" ${pickedId ? "" : "disabled"}>${isLast ? "結果を見る" : "次へ"}</button>
          </div>
          <p class="suo-diag-remaining">${remainText}</p>
        </div>
      </div>
    `, "suo-diag-question");
  }

  function renderLoading() {
    renderShell(`
      <div class="suo-diag-card">
        <div class="suo-diag-loading" role="status">
          <span class="suo-diag-spinner" aria-hidden="true"></span>
          <h2>あなたにぴったりのSUOを選んでいます</h2>
          <p>用途・冷たさ・サイズ・買い替えポイントを見比べて、上位3点を絞り込み中です。</p>
        </div>
      </div>
    `, "suo-diag-loading-view");

    window.setTimeout(renderResult, 1200);
  }

  function renderProductImage(product) {
    return `
      <div class="suo-diag-product-image">
        <img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.name)}" loading="lazy" onerror="this.hidden=true">
      </div>
    `;
  }

  function renderMatchNumber(product, compact) {
    const percent = Math.min(100, Math.max(1, product.matchPercent || 1));
    return `
      <div class="suo-diag-match${compact ? " is-compact" : ""}">
        <span class="suo-diag-match-cap">マッチ度</span>
        <p class="suo-diag-match-num${compact ? " is-compact" : ""}"><span class="suo-diag-match-val" data-count="${percent}">${percent}</span><span class="suo-diag-match-pct">%</span></p>
      </div>
    `;
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }

  function animateMatchCounts() {
    const els = app.querySelectorAll(".suo-diag-match-val");
    if (!window.requestAnimationFrame || prefersReducedMotion()) return;
    els.forEach((el) => {
      const target = parseInt(el.getAttribute("data-count"), 10) || 0;
      const duration = 900;
      let start = null;
      el.textContent = "0";
      const tick = (timestamp) => {
        if (start === null) start = timestamp;
        const progress = Math.min(1, (timestamp - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        el.textContent = String(Math.round(eased * target));
        if (progress < 1) window.requestAnimationFrame(tick);
        else el.textContent = String(target);
      };
      window.requestAnimationFrame(tick);
      // Safety net: guarantee the final value even if rAF is throttled/paused.
      window.setTimeout(() => { el.textContent = String(target); }, duration + 250);
    });
  }

  function getSizeGuide() {
    return data.SIZE_GUIDES[state.userType] || data.SIZE_GUIDES.default;
  }

  function getShareUrls() {
    const text = encodeURIComponent("SUO クール診断でおすすめ商品を見つけました");
    const url = encodeURIComponent(window.location.href.split("#")[0]);
    return {
      x: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      line: `https://social-plugins.line.me/lineit/share?url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`
    };
  }

  function renderMatchBar(product) {
    const percent = Math.min(100, Math.max(1, product.matchPercent || 1));
    return `
      <div class="suo-diag-match-track" aria-hidden="true">
        <span style="width: ${percent}%"></span>
      </div>
    `;
  }

  function renderMatchChips(product, limit) {
    const chips = (product.matchTags || []).slice(0, limit || 6);
    if (!chips.length) return "";
    return `
      <ul class="suo-diag-match-chips" aria-label="合っているポイント">
        ${chips.map((chip) => `<li>${escapeHtml(chip)}</li>`).join("")}
      </ul>
    `;
  }

  function renderSizeHero(product) {
    // Only show a concrete size when the client-defined table resolved one; never
    // guess. On fallback we omit the hero and let the size-selection guide below
    // point the user to the product's size chart.
    const size = product.size;
    if (!size || size.fallback || !size.size_label) return "";
    return `
      <div class="suo-diag-size-hero">
        <p class="suo-diag-size-hero-label">あなたにおすすめのサイズ</p>
        <p class="suo-diag-size-hero-value">${escapeHtml(size.size_label)}</p>
      </div>
    `;
  }

  function renderRecommendedSku(product) {
    const sku = product.recommendedSku;
    if (!sku) return "";
    const options = Object.values(sku.options || {}).filter(Boolean).join(" / ");
    if (!options) return "";
    return `
      <div class="suo-diag-size-hero">
        <p class="suo-diag-size-hero-label">おすすめ仕様</p>
        <p class="suo-diag-size-hero-value">${escapeHtml(options)}</p>
      </div>
    `;
  }

  function renderBestMatch(product) {
    if (!product) return "";
    return `
      <article class="suo-diag-best-card">
        <div class="suo-diag-best-media">
          <p class="suo-diag-best-badge">BEST MATCH</p>
          ${renderProductImage(product)}
        </div>
        <div class="suo-diag-best-body">
          <div class="suo-diag-best-head">
            <p class="suo-diag-rank-text">一番おすすめ</p>
            ${renderMatchNumber(product, false)}
          </div>
          ${renderMatchBar(product)}
          <h3>${escapeHtml(product.name)}</h3>
          ${renderSizeHero(product)}
          ${renderRecommendedSku(product)}
          ${renderMatchChips(product, 6)}
          <p class="suo-diag-product-desc">${escapeHtml(product.description)}</p>
          <div class="suo-diag-best-footer">
            <p class="suo-diag-price">${yen(product.price_from)}〜</p>
            <a class="suo-diag-product-link suo-diag-product-link-primary" href="${escapeHtml(getProductUrl(product))}" target="_blank" rel="noopener" data-product-id="${escapeHtml(product.id)}" data-product-rank="${product.rank}">商品を見る</a>
          </div>
        </div>
      </article>
    `;
  }

  function renderAlternativeCard(product) {
    return `
      <article class="suo-diag-alt-card">
        ${renderProductImage(product)}
        <div class="suo-diag-alt-body">
          <div class="suo-diag-alt-head">
            <h3>${escapeHtml(product.name)}</h3>
            ${renderMatchNumber(product, true)}
          </div>
          ${renderMatchBar(product)}
          ${renderMatchChips(product, 3)}
          ${renderRecommendedSku(product)}
          <p class="suo-diag-product-desc">${escapeHtml(product.description)}</p>
          <div class="suo-diag-alt-footer">
            <p class="suo-diag-price">${yen(product.price_from)}〜</p>
            <a class="suo-diag-product-link suo-diag-product-link-secondary" href="${escapeHtml(getProductUrl(product))}" target="_blank" rel="noopener" data-product-id="${escapeHtml(product.id)}" data-product-rank="${product.rank}">商品を見る</a>
          </div>
        </div>
      </article>
    `;
  }

  function renderAnswerSummary() {
    const tags = getSelectedTags();
    const labels = tags
      .map((tag) => (data.ANSWER_LABELS && data.ANSWER_LABELS[tag]) || null)
      .filter(Boolean);
    if (!labels.length) return "";
    const items = labels.map((label) => `<li>${escapeHtml(label)}</li>`).join("");
    return `
      <section class="suo-diag-answer-summary" aria-label="あなたの回答サマリー">
        <div class="suo-diag-answer-summary-head">
          <h3>Your Answers</h3>
          <span>${labels.length} 項目</span>
        </div>
        <ul class="suo-diag-answer-chips">${items}</ul>
      </section>
    `;
  }

  function renderShareIcons(shares) {
    const icons = {
      x: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18.244 2H21l-6.52 7.45L22.5 22h-6.93l-4.59-6.13L5.5 22H2.74l7.02-8.02L1.5 2h7.04l4.16 5.6L18.24 2zm-2.43 18h1.93L7.27 4H5.25l10.56 16z"/></svg>',
      line: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19.36 10.43c0-3.31-3.32-6.01-7.4-6.01-4.09 0-7.4 2.7-7.4 6.01 0 2.97 2.63 5.46 6.18 5.93.24.05.57.16.65.36.07.18.05.46.02.65l-.1.63c-.03.18-.15.72.63.39.78-.33 4.22-2.49 5.76-4.26 1.06-1.17 1.66-2.36 1.66-3.7zM8.42 12.2H6.94c-.22 0-.4-.17-.4-.39V8.86c0-.22.18-.39.4-.39s.39.17.39.39v2.56h1.09c.22 0 .4.17.4.39 0 .22-.18.39-.4.39zm1.55-.39c0 .22-.18.39-.39.39a.39.39 0 01-.4-.39V8.86c0-.22.18-.39.4-.39s.39.17.39.39v2.95zm3.56 0c0 .17-.11.32-.27.37l-.12.02a.39.39 0 01-.32-.16l-1.51-2.05v1.82c0 .22-.18.39-.39.39a.39.39 0 01-.4-.39V8.86c0-.17.11-.32.27-.37.04-.01.08-.02.12-.02.12 0 .24.06.32.16l1.51 2.05V8.86c0-.22.18-.39.39-.39s.4.17.4.39v2.95zm2.4-1.87c.22 0 .39.18.39.4 0 .21-.17.39-.39.39h-1.09v.69h1.09c.22 0 .39.18.39.4 0 .21-.17.39-.39.39h-1.48a.39.39 0 01-.4-.39V8.86c0-.22.18-.39.4-.39h1.48c.22 0 .39.17.39.39 0 .22-.17.4-.39.4h-1.09v.69h1.09z"/></svg>',
      facebook: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M13.5 22v-8h2.7l.4-3.1h-3.1V8.93c0-.9.25-1.52 1.55-1.52H17V4.64c-.3-.04-1.3-.13-2.46-.13-2.44 0-4.11 1.49-4.11 4.22V10.9H7.7V14h2.73v8h3.07z"/></svg>'
    };
    return `
      <ul class="suo-diag-share-list">
        <li><a href="${shares.x}" target="_blank" rel="noopener" data-network="x" aria-label="Xでシェア">${icons.x}</a></li>
        <li><a href="${shares.line}" target="_blank" rel="noopener" data-network="line" aria-label="LINEでシェア">${icons.line}</a></li>
        <li><a href="${shares.facebook}" target="_blank" rel="noopener" data-network="facebook" aria-label="Facebookでシェア">${icons.facebook}</a></li>
      </ul>
    `;
  }

  function renderResult() {
    const selectedTags = getSelectedTags();
    const recommendations = data.calculateRecommendations(selectedTags, 3);
    state.recommendations = recommendations;
    const summary = data.getSummaryComment(recommendations);
    const note = data.RESULT_NOTES[state.userType] || "";
    const shares = getShareUrls();

    trackEvent("suo_diag_complete", { top_product: recommendations[0] ? recommendations[0].id : "" });
    logEvent("diagnosis_complete", {
      user_type: state.userType,
      gender: genderLabel(),
      answer_summary: readableAnswers(),
      recommended_product_names: recommendations.map((product) => product.name).join(" / "),
      recommended_scores: recommendations.map((product) => `${product.matchPercent}%`).join(" / ")
    });

    const best = recommendations[0];
    const alternatives = recommendations.slice(1).map(renderAlternativeCard).join("");

    renderShell(`
      <div class="suo-diag-card">
        <div class="suo-diag-result-head">
          <h2>あなたにぴったりの一本</h2>
          <p>${escapeHtml(summary)}</p>
          <p class="suo-diag-result-sub">回答条件との一致度が高い順に並べています。<br>未確認の商品条件は一致度に含みません。</p>
        </div>
        ${renderAnswerSummary()}
        ${renderBestMatch(best)}
        ${alternatives ? `
          <section class="suo-diag-alt-section" aria-label="比較候補">
            <div class="suo-diag-alt-title">
              <h3>もう一歩、迷ったら。</h3>
              <p>主役の次に相性の良い2点です。</p>
            </div>
            <div class="suo-diag-alt-grid">
              ${alternatives}
            </div>
          </section>
        ` : ""}
        <section class="suo-diag-guide">
          <h3>サイズ選びの目安</h3>
          <p>${escapeHtml(getSizeGuide())}</p>
        </section>
        ${note ? `<p class="suo-diag-note">${escapeHtml(note)}</p>` : ""}
        <div class="suo-diag-actions">
          <button class="suo-diag-secondary" type="button" data-action="restart">もう一度診断する</button>
        </div>
        <div class="suo-diag-share" aria-label="結果をSNSでシェア">
          <p class="suo-diag-share-label">Share your match</p>
          ${renderShareIcons(shares)}
        </div>
      </div>
    `, "suo-diag-result");

    animateMatchCounts();
  }

  function selectUser(userType) {
    state.userType = userType;

    // Update selection in place so the page doesn't jump to the top on each tap.
    const buttons = app.querySelectorAll("[data-user]");
    if (buttons.length) {
      buttons.forEach((button) => {
        const isSelected = button.getAttribute("data-user") === userType;
        button.classList.toggle("is-selected", isSelected);
        button.setAttribute("aria-pressed", isSelected ? "true" : "false");
      });
      const startButton = app.querySelector('[data-action="start"]');
      if (startButton) startButton.removeAttribute("disabled");
    } else {
      renderStart();
    }
  }

  function start() {
    if (!state.userType) return;
    state.step = 1;
    state.answers = {};
    state.picks = {};
    state.history = ["start"];
    state.recommendations = [];
    if (logger) logger.startSession();
    trackEvent("suo_diag_start", { user_type: state.userType });
    logEvent("diagnosis_start", { user_type: state.userType });
    renderQuestion();
  }

  function selectOption(tag, optionId) {
    const flow = getFlow();
    const question = flow[state.step - 1];
    state.answers[question.key] = tag;
    state.picks[question.key] = optionId;

    // Update selection in place rather than re-rendering the whole screen, so the
    // viewport stays where the user tapped instead of jumping back to the top.
    // Match by the unique option id so duplicate-tag options don't all highlight.
    const card = app.querySelector("[data-q-key]");
    if (card) {
      card.querySelectorAll("[data-option-id]").forEach((button) => {
        const isSelected = button.getAttribute("data-option-id") === optionId;
        button.classList.toggle("is-selected", isSelected);
        button.setAttribute("aria-pressed", isSelected ? "true" : "false");
      });
      const nextButton = card.querySelector('[data-action="next"]');
      if (nextButton) nextButton.removeAttribute("disabled");
    } else {
      renderQuestion();
    }

    // Per-answer events are intentionally NOT logged to the sheet (1 row per user).
    // The full answer list is captured once at diagnosis_complete via answer_summary.
    trackEvent("suo_diag_answer", { question: `Q${state.step}`, tag });
  }

  function advance() {
    const flow = getFlow();
    const question = flow[state.step - 1];
    if (!question || !state.answers[question.key]) return;

    if (state.step >= flow.length) {
      state.history.push(question.key);
      renderLoading();
      return;
    }

    state.history.push(question.key);
    state.step += 1;
    renderQuestion();
  }

  function goBack() {
    if (state.step === 0) {
      // On the "who is it for?" screen -> back to the entry page.
      renderIntro();
      return;
    }
    if (state.step === 1) {
      // On the first question -> back to the "who is it for?" screen.
      state.step = 0;
      state.answers = {};
      state.picks = {};
      renderStart();
      return;
    }

    const flow = getFlow();
    const current = flow[state.step - 1];
    if (current) {
      delete state.answers[current.key];
      delete state.picks[current.key];
    }
    state.step -= 1;
    renderQuestion();
  }

  function restart() {
    state.step = 0;
    state.userType = null;
    state.answers = {};
    state.picks = {};
    state.history = [];
    state.recommendations = [];
    renderIntro();
  }

  app.addEventListener("click", (event) => {
    const userButton = event.target.closest("[data-user]");
    if (userButton) {
      selectUser(userButton.getAttribute("data-user"));
      return;
    }

    const optionButton = event.target.closest("[data-option]");
    if (optionButton) {
      selectOption(optionButton.getAttribute("data-option"), optionButton.getAttribute("data-option-id"));
      return;
    }

    const action = event.target.closest("[data-action]");
    if (action) {
      const name = action.getAttribute("data-action");
      if (name === "begin") renderStart();
      if (name === "start") start();
      if (name === "next") advance();
      if (name === "back") goBack();
      if (name === "restart") restart();
      return;
    }

    const productLink = event.target.closest("[data-product-id]");
    if (productLink) {
      const productId = productLink.getAttribute("data-product-id");
      const rank = productLink.getAttribute("data-product-rank");
      const clicked = state.recommendations.find((product) => product.id === productId);
      trackEvent("suo_diag_click_product", { product_id: productId, rank });
      logEvent("product_click", {
        user_type: state.userType,
        answer_summary: readableAnswers(),
        recommended_product_names: state.recommendations.map((product) => product.name).join(" / "),
        clicked_product_name: clicked ? clicked.name : productId,
        clicked_rank: rank,
        destination_url: productLink.href
      });
    }
  });

  renderIntro();
})();
